"use client"

import { AnimatePresence, motion, type HTMLMotionProps } from "framer-motion"
import { useCallback, useLayoutEffect, useRef, type ReactNode } from "react"

import { DURATION, EASE, RISE_SCALE, RISE_Y, SPRING, staggerDelay } from "@/lib/motion"
import { cn } from "@/lib/utils"

/**
 * The three shapes this app animates things in with, as components.
 *
 * They exist so a page does not have to decide how far something rises or how long it takes.
 * Those decisions are in lib/motion, made once. A survey before writing this found the same
 * fade-and-rise hand-written ten times with five different distances.
 *
 * Deliberately NOT a wrapper for everything with a transition. The research canvas's thinking
 * stream and citation graph are choreography — sequences composed for one screen, where the
 * timings answer to each other rather than to a house style — and squeezing them through these
 * would lose the thing that makes them work. The test is whether a reader would notice if two
 * uses drifted apart. If yes, it belongs here; if no, it is bespoke and should stay bespoke.
 */

type Motionable = Omit<HTMLMotionProps<"div">, "initial" | "animate" | "exit" | "transition">

/**
 * The elements these can render as.
 *
 * A cascade is very often a list, and a `div` inside a `ul` is wrong however it animates — so the
 * tag is a prop rather than something a caller has to work around by wrapping.
 */
const TAGS = {
  div: motion.div,
  li: motion.li,
  p: motion.p,
  span: motion.span,
  section: motion.section,
} as const

type Tag = keyof typeof TAGS

/**
 * Something arriving: fade, with a small rise and scale.
 *
 * `index` turns it into a cascade — pass the position in a list and the delays stagger, capped so
 * a long list still finishes promptly. Without it, one element arriving on its own.
 *
 * `rise={false}` for a plain fade, which is what most of the app's 21 hand-written
 * `initial={{opacity: 0}}` were.
 */
export function Reveal({
  children,
  index,
  rise = true,
  delay = 0,
  duration = DURATION.base,
  as = "div",
  ...props
}: Motionable & {
  children: ReactNode
  index?: number
  rise?: boolean
  delay?: number
  duration?: number
  as?: Tag
}) {
  const from = rise ? { opacity: 0, y: RISE_Y, scale: RISE_SCALE } : { opacity: 0 }
  const to = rise ? { opacity: 1, y: 0, scale: 1 } : { opacity: 1 }
  const Component = TAGS[as] as typeof motion.div

  return (
    <Component
      initial={from}
      animate={to}
      exit={{ ...from, y: rise ? -RISE_Y : undefined, transition: { duration: DURATION.quick, ease: "easeOut" } }}
      transition={{
        duration,
        ease: EASE,
        delay: delay + (index === undefined ? 0 : staggerDelay(index)),
        /*
          Layout gets its own transition, and deliberately no delay.

          A flat `transition` in framer applies to the LAYOUT animation as well as the entrance,
          so the stagger above was also staggering rearrangement: rows already on screen each
          started moving at a different moment, which is not a cascade, it is a shuffle. A
          rearrangement should happen at once — the stagger is about things ARRIVING.
        */
        layout: { duration: DURATION.base, ease: EASE },
      }}
      {...props}
    >
      {children}
    </Component>
  )
}

/**
 * One element that swaps its contents.
 *
 * `key` is the whole mechanism: change it and the old contents fade out before the new fade in.
 * For a number or a label that changes under something still moving, where an instant swap reads
 * as a glitch.
 */
export function Crossfade({
  children,
  identity,
  className,
}: {
  children: ReactNode
  /** Changing this is what triggers the swap. */
  identity: string | number
  className?: string
}) {
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={identity}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: DURATION.cross, ease: "easeOut" }}
        className={className}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  )
}

/**
 * Something that stays where it is and fades out of the way.
 *
 * The one shape `Reveal` cannot cover: it animates on MOUNT, and this is for a surface that has
 * to stay mounted while it is hidden. The reader's document map is the case that named it — the
 * rail keeps its scroll position and its IntersectionObserver, so unmounting it would throw both
 * away and remounting would pop it back with no transition.
 *
 * Here rather than written out at each call site because there are already three of them and
 * they had drifted: the map rail faded over 200ms in CSS, the landing canvas's foot over 300ms
 * in framer, each with its own easing. That is exactly the test in this file's description — two
 * uses a reader WOULD notice drifting apart.
 *
 * `pointer-events-none` comes with it. A surface at zero opacity still catches clicks, and every
 * hand-written version of this had to remember that separately.
 *
 * Visibility is deferred with `transitionEnd` rather than set alongside the opacity, so hiding
 * runs the fade first and only then takes the element out of the tree — set together, it would
 * vanish instantly and the fade would never be seen. Showing flips it immediately, since there
 * is nothing to wait for.
 */
export function Fade({
  show,
  children,
  as = "div",
  className,
  ...props
}: Motionable & {
  children: ReactNode
  /** Whether the surface is on screen. */
  show: boolean
  as?: Tag
  className?: string
}) {
  const Component = TAGS[as] as typeof motion.div

  return (
    <Component
      // No entrance on mount: a page that loads with the surface already hidden should draw it
      // hidden, not fade it out in front of the reader.
      initial={false}
      animate={
        show
          ? { opacity: 1, visibility: "visible" }
          : { opacity: 0, transitionEnd: { visibility: "hidden" } }
      }
      // Leaving is quicker than arriving, as everywhere else here.
      transition={{ duration: show ? DURATION.base : DURATION.quick, ease: EASE }}
      className={cn(!show && "pointer-events-none", className)}
      {...props}
    >
      {children}
    </Component>
  )
}

/**
 * Something appearing with weight, on a spring rather than a curve.
 *
 * For controls that appear in response to what the reader just did — a send button once the field
 * has text — where a fade reads as the app catching up and a spring reads as the control being
 * ready. Springs are for reactions; `Reveal` is for arrivals.
 */
export function Pop({
  children,
  spring = "pop",
  as = "div",
  ...props
}: Motionable & { children: ReactNode; spring?: keyof typeof SPRING; as?: Tag }) {
  const Component = TAGS[as] as typeof motion.div

  return (
    <Component
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={SPRING[spring]}
      {...props}
    >
      {children}
    </Component>
  )
}

/**
 * A box that grows and shrinks to fit whatever is inside it.
 *
 * For a surface whose content changes size after it is already on screen — a panel that opens
 * before its text has loaded, a section that expands. Without it the box snaps to its new size
 * the instant the content lands, which on something the reader is already looking at is the
 * jarring half of "make room, then arrive".
 *
 * Height is written straight to the node and transitioned in CSS rather than held in state:
 * a ResizeObserver firing setState would re-render the subtree on every size change, and the
 * height is not something any other component needs to know. The first measurement is applied
 * with the transition still off, so a box does not animate up from nothing on mount.
 *
 * Pair it with a delayed `Reveal` inside for the second half — the content arriving once the box
 * that holds it has stopped moving.
 */
export function AutoHeight({
  children,
  className,
  duration = DURATION.base,
}: {
  children: ReactNode
  className?: string
  duration?: number
}) {
  const outer = useRef<HTMLDivElement>(null)
  const inner = useRef<HTMLDivElement>(null)
  const measured = useRef(false)

  const apply = useCallback(() => {
    const box = outer.current
    const content = inner.current
    if (!box || !content) return
    // The transition goes on BEFORE the height that should animate, and not at all for the first
    // measurement — otherwise the box animates up from nothing the moment it mounts.
    if (measured.current) {
      box.style.transition = `height ${duration}s cubic-bezier(${EASE.join(",")})`
    }
    box.style.height = `${content.offsetHeight}px`
    measured.current = true
  }, [duration])

  /*
    Measured after every render, which is when the content has actually been swapped.

    A ResizeObserver alone was the obvious implementation and is not enough on its own: its
    callbacks are delivered as part of the rendering lifecycle, so in a backgrounded tab they do
    not arrive — measured here, a card whose text landed while hidden kept its loading height
    while its content was already 254px tall. Doing the work in a layout effect makes the common
    case — new children — synchronous and independent of whether anything is being painted.
  */
  useLayoutEffect(apply)

  /*
    The observer stays, for size changes that happen WITHOUT a render: a webfont swapping in and
    rewrapping the text, an image finishing, the panel being resized around it. Nothing re-renders
    for those, so a layout effect would never see them.
  */
  useLayoutEffect(() => {
    const content = inner.current
    if (!content) return
    const observer = new ResizeObserver(apply)
    observer.observe(content)
    return () => observer.disconnect()
  }, [apply])

  /*
    The inner element is what gets measured, and `overflow-hidden` on it is load-bearing: without
    it a child's vertical margins collapse THROUGH it and are not counted in `offsetHeight`, so a
    box wrapping anything with `my-*` measures short — a hairline rule with `my-2` measured 1px.
    Containing the margins is what makes the measurement mean what it says.
  */
  return (
    <div ref={outer} className={cn("overflow-hidden", className)}>
      <div ref={inner} className="overflow-hidden">
        {children}
      </div>
    </div>
  )
}
